// libs
import * as z from 'zod'
// types
import { ChartConfig, ChartConfigInput, ChartQuery, ChartType } from 'types/charts'
import { IChart, ChartAggregate, ChartDateBucket, IChartDataInput } from 'types/graphql'
// constants
import { CREATED_AT_FIELD_ID, CHART_TYPE_DEFAULTS } from 'constants/charts'
// schemas
import { ChartConfigSchema, GROUP_BY_COUNTS } from 'schemas/chartConfig'
// utils
import { cloneDeep, isPlainObject, mergeWith, omit } from 'lodash'

// Arrays replace arrays: a custom palette or groupBy is never merged item by item
const replaceArrays = (_: unknown, source: unknown) => (Array.isArray(source) ? source : undefined)

export const mergeChartObjects = <T>(target: T, ...sources: unknown[]): T =>
  mergeWith(cloneDeep(target), ...sources.filter(isPlainObject).map(cloneDeep), replaceArrays)

const DEFAULT_QUERY: ChartQuery = {
  riskTemplateId: null,
  groupBy: [],
  aggregate: ChartAggregate.Count,
  aggregateFieldId: null,
  filterId: null
}

const CHART_TYPES = Object.values(ChartType)

const getChartType = (config: ChartConfigInput, type: ChartType) =>
  CHART_TYPES.includes(config.type) ? config.type : type

// The config as the schema reads it: the chart type's defaults under it, the query apart
const withTypeDefaults = (config: ChartConfigInput, type: ChartType) =>
  mergeChartObjects(CHART_TYPE_DEFAULTS[type], omit(config, 'query'), { type })

// Whether the schema takes the config as it is, e.g. the settings form's while it is edited
export const isChartConfigValid = (config: ChartConfigInput | null | undefined, type: ChartType = ChartType.Bar) =>
  ChartConfigSchema.safeParse(withTypeDefaults(config || {}, getChartType(config || {}, type))).success

// The schema refuses a wrong value: such a config is drawn from its type's defaults, never half-applied
const parseChartConfig = (config: ChartConfigInput, type: ChartType) => {
  const parsed = ChartConfigSchema.safeParse(withTypeDefaults(config, type))

  if (parsed.success) {
    return parsed.data
  }

  console.error('[parseChartConfig]', z.prettifyError(parsed.error))

  return ChartConfigSchema.parse(mergeChartObjects(CHART_TYPE_DEFAULTS[type], { type }))
}

// Fills a stored, proposed or partial config up to a full one: the chart type's defaults, then the schema's.
// The query is the form's own: a new chart has no template yet, so it is filled in, not parsed.
export const normalizeChartConfig = (
  config: ChartConfigInput | null | undefined,
  type: ChartType = ChartType.Bar
): ChartConfig => {
  const input = config || {}

  return {
    ...parseChartConfig(input, getChartType(input, type)),
    query: { ...DEFAULT_QUERY, ...input.query }
  }
}

// A saved chart as the tile and the form use it: its query back inside the config
export const getSavedChartConfig = (chart: IChart): ChartConfig =>
  normalizeChartConfig({ ...chart.config, query: chart.query })

// The chartPreview / chartCreate input: only the input's fields, so no __typename of the cache goes along
export const getChartDataInput = (query: ChartQuery): IChartDataInput => ({
  riskTemplateId: query.riskTemplateId,
  groupBy: (query.groupBy || []).map(({ fieldId, dateBucket }) => (dateBucket ? { fieldId, dateBucket } : { fieldId })),
  aggregate: query.aggregate,
  aggregateFieldId: query.aggregateFieldId || null,
  filterId: query.filterId || null
})

// Drops what the server would reject as CHART_QUERY_INVALID: empty group-by rows, rows past GROUP_BY_COUNTS of the
// type, a value field for COUNT.
// The form sets dateBucket when a date field is picked and clears it otherwise.
export const cleanChartQuery = (config: ChartConfig): ChartConfig => {
  const groupBy = (config.query.groupBy || [])
    .filter((item) => item?.fieldId)
    .slice(0, Math.max(...GROUP_BY_COUNTS[config.type]))
    .map(({ fieldId, dateBucket }) =>
      dateBucket || fieldId === CREATED_AT_FIELD_ID
        ? { fieldId, dateBucket: dateBucket || ChartDateBucket.Month }
        : { fieldId }
    )

  const isCount = config.query.aggregate === ChartAggregate.Count

  return {
    ...config,
    query: {
      ...config.query,
      groupBy,
      aggregateFieldId: isCount ? null : config.query.aggregateFieldId || null
    }
  }
}

export const isChartQueryReady = (config?: ChartConfig | null) => {
  const query = config?.query

  if (!query?.riskTemplateId || !query.aggregate) {
    return false
  }
  if (query.aggregate !== ChartAggregate.Count && !query.aggregateFieldId) {
    return false
  }
  if (!GROUP_BY_COUNTS[config.type].includes(query.groupBy?.length || 0)) {
    return false
  }
  return true
}
